import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'underscore';

import { TagModel } from '../../../../../../../typings/api/skymap/rest/v0/.common';
import { FolderModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { errorUtils, getResponseErrorParameters } from '../../../../api/error-handling';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import { TagsTable } from './tags-table';

interface Props {
  folder: FolderModel;
  onClose: (tagsChanged: boolean) => void;
}

export type CheckableTag = TagModel & { checked: boolean; validationNotFound: boolean };

enum Status {
  LoadingTags,
  Saving,
  Error,
}

const FolderTagsDialog = (props: Props) => {
  const { t } = useTranslation();
  const { project } = React.useContext(ProjectContext);

  const [status, setStatus] = React.useState(Status.LoadingTags);
  const [tags, setTags] = React.useState<CheckableTag[]>([]);
  const initialSelectedTagIds = React.useRef<string[]>([]);
  const [saveButtonEnabled, setSaveButtonEnabled] = React.useState(false);

  React.useEffect(() => {
    // Get tags from project.
    // Tag is checked if the same tag is passed as props to the component.
    const tags: CheckableTag[] =
      project.tags?.map((projectTag) => ({
        ...projectTag,
        checked: !!props.folder.tags?.find((folderTag) => folderTag.id === projectTag.id),
        validationNotFound: false,
      })) ?? [];

    // Set tags.
    setTags(tags);

    // Save list ids of initially selected tags.
    // This is used to decide whether the Save button should be enabled or not.
    initialSelectedTagIds.current = tags.filter((x) => x.checked).map((x) => x.id);
  }, [project.tags, props.folder.tags]);

  const selectionChanged = (list1: string[], list2: string[]) =>
    list1.length !== list2.length || _.intersection(list1, list2).length !== list1.length;

  const onTagSelectionChanged = React.useCallback(
    (newIdList: string[]) => {
      setTags(
        tags.map((tag) => ({
          ...tag,
          checked: newIdList.includes(tag.id),
        })),
      );

      setSaveButtonEnabled(selectionChanged(initialSelectedTagIds.current, newIdList));
    },
    [tags],
  );

  const saveButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      const folderService = SkyMapAxiosServiceFactory.instance.createFolderServiceV1();
      const selectedTagIds = tags.filter((x) => x.checked).map((x) => x.id);

      await folderService.patchFolder({
        path: { folderId: props.folder.id },
        body: { tagIds: [...selectedTagIds] },
      });

      props.onClose(true);
    } catch (err) {
      handleCreateFolderError(err);
    }
  };

  const handleCreateFolderError = (err: unknown) => {
    setStatus(Status.Error);

    if (errorUtils.isResponseError(err, 'validation_failed', 'tag:tags_not_found')) {
      const parameters = getResponseErrorParameters(err, 'validation_failed', 'tag:tags_not_found');

      // Parameter "tagIds" should exist for this error code.
      const tagIds = parameters!.find((x) => x.name === 'tagIds')!.value as string;
      setTags(
        tags.map((tag) => ({
          ...tag,
          validationNotFound: tagIds.includes(tag.id),
        })),
      );
    } else {
      setTags(
        tags.map((tag) => ({
          ...tag,
          validationNotFound: false,
        })),
      );
    }
  };

  const cancelButtonClick = () => {
    props.onClose(false);
  };

  const renderInfoBox = () => {
    switch (status) {
      case Status.LoadingTags:
        return (
          <InfoBox bottomMargin={true} color="yellow">
            {t('fileManager.folderTagsDialog.initial', { ns: 'components' })}
          </InfoBox>
        );
      case Status.Saving:
        return (
          <InfoBox bottomMargin={true} color="yellow" leftIcon={{ icon: 'spinner', spin: true }}>
            {t('fileManager.folderTagsDialog.saving', { ns: 'components' })}
          </InfoBox>
        );
      case Status.Error:
        return tags.some((x) => x.validationNotFound) ? (
          <InfoBox bottomMargin={true} color="red" leftIcon={{ icon: 'exclamation' }}>
            <Trans
              components={{ br: <br /> }}
              i18nKey="fileManager.folderTagsDialog.errorTagsNoLongerExist"
              ns="components"
            />
          </InfoBox>
        ) : (
          <InfoBox bottomMargin={true} color="red" leftIcon={{ icon: 'exclamation' }}>
            <Trans
              components={{ br: <br /> }}
              i18nKey="fileManager.folderTagsDialog.unknownError"
              ns="components"
            />
          </InfoBox>
        );
    }
  };

  return (
    <Dialog
      closeIcon={status !== Status.Saving}
      closeOnDimmerClick={true}
      width={350}
      onClose={cancelButtonClick}
    >
      {{
        header: t('fileManager.folderTagsDialog.title', { ns: 'components' }),
        content: (
          <>
            {tags.length === 0 ? (
              <InfoBox color="yellow">
                {t('fileManager.folderTagsDialog.projectHasNoTags', { ns: 'components' })}
              </InfoBox>
            ) : (
              <>
                {renderInfoBox()}

                <TagsTable
                  disabled={status === Status.Saving}
                  tags={tags}
                  onSelectionChanged={onTagSelectionChanged}
                />
              </>
            )}
          </>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={!saveButtonEnabled || status === Status.Saving}
                variant="contained"
                onClick={saveButtonClick}
              >
                {t('save', { ns: 'common' })}
              </Button>
              <Button
                disabled={status === Status.Saving}
                variant="text"
                onClick={cancelButtonClick}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { FolderTagsDialog };
